// src/components/themes/default/admin/BranchAttendanceTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  Calendar,
  CheckCircle2,
  Clock,
  Save,
  Loader2,
  RefreshCw,
  Users,
  AlertCircle,
  Crown,
  CalendarCheck,
} from "lucide-react";

interface BranchAttendanceTabProps {
  branchInfo: any;
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
}

export default function BranchAttendanceTab({
  branchInfo,
  subdomain,
  theme = "dark",
  primaryColor = "#059669",
}: BranchAttendanceTabProps) {
  const isLight = theme === "light";
  const branchId = branchInfo?.id;
  const branchName = branchInfo?.name || "Branch Outlet";

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [employees, setEmployees] = useState<any[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getBranchToken = useCallback(() => {
    if (typeof window === "undefined") return "";
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token) return data.token;
        }
      }
    } catch {}
    return localStorage.getItem("token") || "";
  }, []);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const fetchData = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const token = getBranchToken();
      const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Branch-ID": String(branchId),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // ১. সুপার ম্যানেজারসহ ব্রাঞ্চের সব কর্মীদের ফেচ করা
      const empRes = await fetch(
        getApiUrl(`/api/v1/hrm/employees/?branch_id=${branchId}`),
        { headers }
      );
      let empList: any[] = [];
      if (empRes.ok) {
        const data = await empRes.json();
        empList = Array.isArray(data) ? data : data.results || [];
        setEmployees(empList);
      }

      // ২. নির্বাচিত তারিখের হাজিরা রেকর্ড
      const attRes = await fetch(
        getApiUrl(`/api/v1/hrm/attendance/?branch_id=${branchId}&date=${selectedDate}`),
        { headers }
      );
      const recMap: Record<number, any> = {};
      if (attRes.ok) {
        const aData = await attRes.json();
        const aList = Array.isArray(aData) ? aData : aData.results || [];
        aList.forEach((item: any) => {
          recMap[item.employee] = item;
        });
      }

      // ৩. স্টেট ইনিশিয়ালাইজেশন
      const initialMap: Record<number, any> = {};
      empList.forEach((emp) => {
        if (recMap[emp.id]) {
          initialMap[emp.id] = recMap[emp.id];
        } else {
          initialMap[emp.id] = {
            employee: emp.id,
            status: "present",
            clock_in: "09:30:00",
            notes: "",
          };
        }
      });
      setAttendanceRecords(initialMap);
    } catch (err) {
      console.error("Error loading attendance:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiUrl, getBranchToken, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (empId: number, newStatus: string) => {
    setAttendanceRecords((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        status: newStatus,
      },
    }));
  };

  const handleClockInChange = (empId: number, val: string) => {
    setAttendanceRecords((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        clock_in: val,
      },
    }));
  };

  // 🌟 সুপার ম্যানেজার ও সব কর্মচারীদের হাজিরা বাল্ক সেভ
  const handleSaveAttendance = async () => {
    setSaving(true);
    try {
      const payloadRecords = Object.values(attendanceRecords).map((rec: any) => ({
        employee_id: rec.employee,
        status: rec.status,
        clock_in: rec.clock_in,
        notes: rec.notes || "",
      }));

      const token = getBranchToken();
      const res = await fetch(getApiUrl("/api/v1/hrm/attendance/bulk-mark/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          date: selectedDate,
          records: payloadRecords,
        }),
      });

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "উপস্থিতি সংরক্ষিত!",
          text: `সুপার ম্যানেজারসহ সবার হাজিরা সফলভাবে সেভ হয়েছে।`,
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchData();
      }
    } catch {
      Swal.fire({ icon: "error", title: "ব্যর্থ হয়েছে" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 font-sans animate-in fade-in pb-12">
      {/* 🌟 Top Header */}
      <div
        className={`p-6 rounded-3xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
          isLight
            ? "bg-white border-slate-200 shadow-sm text-slate-900"
            : "bg-slate-900 border-slate-800 text-white shadow-xl"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
            className="w-12 h-12 rounded-2xl flex items-center justify-center border"
          >
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-black flex items-center gap-2">
              <span>Daily Team Attendance Sheet</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-500 font-mono">
                {employees.length} Members
              </span>
            </h1>
            <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
              {branchName} • ব্রাঞ্চ সুপার ম্যানেজার ও কর্মীদের প্রতিদিনের উপস্থিতি গ্রহণ
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold border outline-none font-mono ${
              isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800 text-white"
            }`}
          />

          <button
            onClick={handleSaveAttendance}
            disabled={saving || loading || employees.length === 0}
            style={{ backgroundColor: primaryColor }}
            className="px-5 py-2.5 rounded-xl text-xs font-bold text-white shadow-lg flex items-center gap-2 cursor-pointer hover:opacity-90 active:scale-95 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>Save Attendance</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p className="text-xs font-bold text-slate-400">Loading team attendance sheet...</p>
        </div>
      ) : employees.length === 0 ? (
        <div
          className={`p-12 text-center rounded-3xl border-2 border-dashed space-y-3 ${
            isLight ? "bg-white border-slate-200 text-slate-500" : "bg-slate-900/60 border-slate-800 text-slate-400"
          }`}
        >
          <CalendarCheck className="w-10 h-10 mx-auto text-slate-400 opacity-60" />
          <h4 className="text-sm font-black">হাজিরা নেওয়ার মতো কোনো কর্মী পাওয়া যায়নি</h4>
          <p className="text-xs max-w-sm mx-auto">
            ব্রাঞ্চের রিফ্রেশ বোতামে ক্লিক করুন অথবা মেইন মার্চেন্ট থেকে কর্মী নিয়োগ নিশ্চিত করুন।
          </p>
        </div>
      ) : (
        <div
          className={`p-6 rounded-3xl border space-y-4 overflow-x-auto ${
            isLight ? "bg-white border-slate-200 shadow-xs" : "bg-slate-900 border-slate-800 text-white"
          }`}
        >
          <table className="w-full text-left text-xs min-w-[700px] border-collapse">
            <thead>
              <tr className="border-b text-[10px] font-black uppercase text-slate-400 tracking-wider">
                <th className="py-3 px-4">EMPLOYEE</th>
                <th className="py-3 px-4">ROLE & DEPARTMENT</th>
                <th className="py-3 px-4">ATTENDANCE STATUS</th>
                <th className="py-3 px-4">IN-TIME</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
              {employees.map((emp) => {
                const isMgr = Boolean(emp.is_manager);
                const rec = attendanceRecords[emp.id] || { status: "present", clock_in: "09:30:00" };

                return (
                  <tr
                    key={emp.id}
                    className={`transition-colors ${
                      isMgr
                        ? isLight
                          ? "bg-emerald-50/40 hover:bg-emerald-50/70"
                          : "bg-emerald-950/20 hover:bg-emerald-950/30"
                        : "hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
                    }`}
                  >
                    <td className="py-4 px-4">
                      <div className="font-bold flex items-center gap-2">
                        <span className="text-slate-900 dark:text-white font-black">{emp.full_name}</span>
                        {isMgr && (
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-500 text-white flex items-center gap-1 shadow-xs">
                            <Crown className="w-2.5 h-2.5" /> Super Manager
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">#{emp.employee_id}</span>
                    </td>

                    <td className="py-4 px-4">
                      <div className="font-bold text-slate-800 dark:text-slate-200">
                        {emp.designation_title || (isMgr ? "Branch Super Manager" : "Staff")}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {emp.department_name || "Store Operations"}
                      </div>
                    </td>

                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1.5">
                        {["present", "late", "absent"].map((st) => (
                          <button
                            key={st}
                            type="button"
                            onClick={() => handleStatusChange(emp.id, st)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                              rec.status === st
                                ? st === "present"
                                  ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20"
                                  : st === "late"
                                  ? "bg-amber-500 text-white shadow-md shadow-amber-500/20"
                                  : "bg-rose-500 text-white shadow-md shadow-rose-500/20"
                                : isLight
                                ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                          >
                            {st}
                          </button>
                        ))}
                      </div>
                    </td>

                    <td className="py-4 px-4">
                      <input
                        type="time"
                        value={rec.clock_in ? rec.clock_in.slice(0, 5) : "09:30"}
                        onChange={(e) => handleClockInChange(emp.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-mono font-bold outline-none ${
                          isLight
                            ? "bg-slate-50 border-slate-200 text-slate-900"
                            : "bg-slate-950 border-slate-800 text-white"
                        }`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}